import type { ManagerOptions, SocketOptions } from 'socket.io-client';
import type { SocketEvent, SocketNamespace } from '@trinity/chat';

/**
 * Check if WebSocket debug logging is enabled.
 * Enable by running in console: localStorage.setItem('ws:debug', 'true')
 * Then reload the page.
 */
export const isDebugEnabled = () => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('ws:debug') === 'true';
};

type LoggerOptions = {
  namespace: SocketNamespace;
};

type Logger = (data: LoggerOptions) => (event: SocketEvent, data?: unknown) => void;

const logEvent: Logger = namespace => (event, data) => {
  const types = {
    connect: logInfo,
    disconnect: logWarn,
    authenticated: logInfo,
    unauthorized: logWarn,
    connect_error: logError,
  } as Record<string, Logger>;

  const logger: Logger = types[event] || logInfo;
  logger(namespace)(event, data);
};

const logInfo: Logger = l => (event, data) => {
  if (!isDebugEnabled()) return;

  console.groupCollapsed(
    `%c socket:${l.namespace} %c${event.toUpperCase()}`,
    'color: #979797;',
    'color: #00bcd4; font-weight: bold;',
  );
  if (data) console.info(data);
  console.groupEnd();
};

const logWarn: Logger = l => (event, data) => {
  if (!isDebugEnabled()) return;

  console.groupCollapsed(
    `%c socket:${l.namespace} %c${event.toUpperCase()}`,
    'color: #979797;',
    'color: #00bcd4; font-weight: bold;',
  );
  if (data) console.info(data);
  console.groupEnd();
};

const logError = (l: LoggerOptions) => (message?: any, ...optionalParams: any[]) => {
  // Always log errors, even in production
  console.error(
    `[WebSocket Error] socket:${l.namespace} ${message}`,
    ...optionalParams,
  );
};

const logGeneric = (l: LoggerOptions) => (value: string) => {
  if (!isDebugEnabled()) return;

  console.log(
    `%csocket: ${l.namespace}%c ${value}`,
    'color: #00bcd4; font-weight: bold;',
    'color: inherit;',
  );
};

export const createLogger = (opts: LoggerOptions) => ({
  event: logEvent(opts),
  log: logGeneric(opts),
  info: logInfo(opts),
  warn: logWarn(opts),
  error: logError(opts),
});

export function resolveSocketOptions(opts: ResolveSocketOptionsParams): Partial<ManagerOptions & SocketOptions> {
  const options: Partial<ManagerOptions & SocketOptions> = {
    path: opts.socketPath,
    withCredentials: true,
    transports: ['websocket', 'polling'],
    ...opts,
  };

  // Include session ID in auth if available
  if (opts.sessionId) {
    options.auth = {
      ...(options.auth || {}),
      ['X-SID']: opts.sessionId,
    };

    if (isDebugEnabled()) {
      console.log('[WS Config] Session ID included in auth:', opts.sessionId.substring(0, 8) + '...');
    }
  } else if (isDebugEnabled()) {
    console.warn('[WS Config] No session ID available for WebSocket auth');
  }

  if (isDebugEnabled()) {
    console.log('[WS Config] Socket options:', {
      path: options.path,
      withCredentials: options.withCredentials,
      transports: options.transports,
      hasAuth: !!options.auth,
    });
  }

  return options;
}

export type ResolveSocketOptionsParams = {
  socketPath: string;
  sessionId?: string;
} & Partial<ManagerOptions & SocketOptions>;